import { useState, useRef } from "react"
import { router, usePage } from "@inertiajs/react"
import { track } from "@/lib/analytics"
import type { SharedProps, SocialConnection } from "@/types"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import PlatformCaptionTabs from "@/components/dashboard/posts/platform-caption-tabs"
import type { LocationMeta } from "@/components/dashboard/posts/location-picker"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import {
  INITIAL_TIKTOK_STATE,
  isTiktokStateValid,
  type TiktokPostState,
} from "@/lib/tiktok-post-state"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

type Platform = "instagram" | "facebook" | "tiktok" | "youtube"
type AspectRatio = "portrait" | "landscape" | "square"

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
}

const ASPECT_RATIOS: { value: AspectRatio; label: string; ratio: string }[] = [
  { value: "portrait", label: "Portrait", ratio: "9:16" },
  { value: "landscape", label: "Landscape", ratio: "16:9" },
  { value: "square", label: "Square", ratio: "1:1" },
]

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function NewVideoPostDialog({
  connections,
  open,
  onOpenChange,
}: {
  connections: SocialConnection[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { currentUser } = usePage<SharedProps>().props
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set())
  const [captions, setCaptions] = useState<Record<Platform, string>>({
    instagram: "",
    facebook: "",
    tiktok: "",
    youtube: "",
  })
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("portrait")
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined)
  const [publishing, setPublishing] = useState(false)
  const [generatingCaption, setGeneratingCaption] = useState<Record<string, boolean>>({})
  const [facebookPageId, setFacebookPageId] = useState<string>("")
  const [facebookPostType, setFacebookPostType] = useState<"reel" | "video">("reel")
  const [shareToFeed, setShareToFeed] = useState(true)
  const [tiktokState, setTiktokState] = useState<TiktokPostState>(INITIAL_TIKTOK_STATE)
  const [locationId, setLocationId] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)
  const [locationMeta, setLocationMeta] = useState<LocationMeta | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fbConnection = connections.find(
    (c) => c.platform === "facebook" && c.status === "active",
  )
  const fbPages = fbConnection?.managed_pages || []

  const supportedConnections = connections.filter(
    (c) =>
      (c.platform === "facebook" ||
        c.platform === "instagram" ||
        c.platform === "tiktok" ||
        c.platform === "youtube") &&
      c.status === "active",
  )

  function togglePlatform(platform: Platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  function handleVideoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      toast.error("Video must be less than 100MB.")
      return
    }

    if (!["video/mp4", "video/quicktime", "video/webm"].includes(file.type)) {
      toast.error("Please upload an MP4, MOV, or WebM file.")
      return
    }

    setVideoFile(file)
  }

  function removeVideo() {
    setVideoFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function resetForm() {
    setSelectedPlatforms(new Set())
    setCaptions({ instagram: "", facebook: "", tiktok: "", youtube: "" })
    removeVideo()
    setAspectRatio("portrait")
    setScheduleEnabled(false)
    setScheduledAt(undefined)
    setFacebookPageId("")
    setFacebookPostType("reel")
    setShareToFeed(true)
    setTiktokState(INITIAL_TIKTOK_STATE)
    setLocationId(null)
    setLocationName(null)
    setLocationMeta(null)
  }

  async function handlePublish() {
    if (selectedPlatforms.size === 0) return
    if (!videoFile) {
      toast.error("Please upload a video file.")
      return
    }

    setPublishing(true)
    try {
      const formData = new FormData()
      formData.append("post_type", "video")
      formData.append("video_file", videoFile)
      formData.append("aspect_ratio", aspectRatio)

      for (const p of selectedPlatforms) {
        formData.append("platforms[]", p)
        formData.append(`captions[${p}]`, captions[p])
      }
      if (scheduleEnabled && scheduledAt) {
        formData.append("scheduled_at", scheduledAt.toISOString())
      }
      if (selectedPlatforms.has("facebook") && facebookPageId) {
        formData.append("facebook_page_id", facebookPageId)
      }
      if (selectedPlatforms.has("facebook")) {
        formData.append("facebook_post_type", facebookPostType)
      }
      if (selectedPlatforms.has("instagram")) {
        formData.append("share_to_feed", String(shareToFeed))
      }
      if (selectedPlatforms.has("tiktok")) {
        formData.append("tiktok_privacy", tiktokState.privacy)
        formData.append("tiktok_disable_comment", String(!tiktokState.allowComments))
        formData.append("tiktok_disable_duet", String(!tiktokState.allowDuet))
        formData.append("tiktok_disable_stitch", String(!tiktokState.allowStitch))
        formData.append("tiktok_brand_content_toggle", String(tiktokState.brandedContent))
        formData.append("tiktok_brand_organic_toggle", String(tiktokState.yourBrand))
      }
      if (locationId) {
        formData.append("location_id", locationId)
      }
      if (locationName) {
        formData.append("location_name", locationName)
      }
      if (locationMeta?.address) {
        formData.append("location_address", locationMeta.address)
      }
      if (locationMeta?.verified) {
        formData.append("location_verified", "true")
      }
      if (locationMeta?.picture_url) {
        formData.append("location_picture_url", locationMeta.picture_url)
      }
      if (locationMeta?.hours) {
        formData.append("location_hours", locationMeta.hours)
      }

      const response = await fetch("/dashboard/posts", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
        },
        body: formData,
      })

      if (response.ok) {
        track("standalone_video_post_published", {
          platforms: Array.from(selectedPlatforms),
          scheduled: scheduleEnabled,
          aspect_ratio: aspectRatio,
        })
        toast.success(
          scheduleEnabled
            ? "Video posts scheduled successfully"
            : selectedPlatforms.has("tiktok")
              ? "Publishing video. TikTok may take a few minutes to process your video."
              : "Publishing video to social media...",
        )
        onOpenChange(false)
        resetForm()
        router.reload()
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to create video posts")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setPublishing(false)
    }
  }

  async function handleGenerateCaption(platform: string) {
    setGeneratingCaption((prev) => ({ ...prev, [platform]: true }))
    try {
      const response = await fetch("/dashboard/posts/generate_caption", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ platform }),
      })

      if (response.ok) {
        const data = await response.json()
        setCaptions((prev) => ({ ...prev, [platform]: data.caption }))
        track("standalone_video_caption_generated", { platform })
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to generate caption")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setGeneratingCaption((prev) => ({ ...prev, [platform]: false }))
    }
  }

  const hasConnections = supportedConnections.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New video post</DialogTitle>
          <DialogDescription>
            Upload a video and publish it to your social media accounts.
          </DialogDescription>
        </DialogHeader>

        {!hasConnections ? (
          <NoConnectionsCallout />
        ) : (
          <>
            {/* Video upload */}
            <div className="space-y-2 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
                Video
              </p>
              {videoFile ? (
                <div className="flex items-center gap-3 rounded-lg border border-stone/30 bg-stone/5 px-4 py-3">
                  <svg
                    className="size-8 shrink-0 text-warm-grey"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                    />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-charcoal">
                      {videoFile.name}
                    </p>
                    <p className="text-xs text-warm-grey">
                      {formatFileSize(videoFile.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="rounded-full p-1 text-warm-grey transition-colors hover:bg-stone/20 hover:text-charcoal"
                  >
                    <svg
                      className="size-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-stone/40 px-4 py-8 text-sm text-warm-grey transition-colors hover:border-stone/60 hover:bg-stone/5"
                >
                  <svg
                    className="size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                    />
                  </svg>
                  Click to upload a video
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                onChange={handleVideoChange}
                className="hidden"
              />
              <p className="text-[10px] text-warm-grey/60">
                MP4, MOV, or WebM. Max 100MB.
              </p>
            </div>

            {/* Aspect ratio */}
            <div className="space-y-2 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
                Aspect ratio
              </p>
              <div className="grid grid-cols-3 gap-2">
                {ASPECT_RATIOS.map((ar) => (
                  <button
                    key={ar.value}
                    type="button"
                    onClick={() => setAspectRatio(ar.value)}
                    className={`rounded-lg border px-3 py-2 text-center text-sm transition-all ${
                      aspectRatio === ar.value
                        ? "border-red/50 bg-red/5 font-medium text-charcoal"
                        : "border-stone/60 text-warm-grey hover:border-stone"
                    }`}
                  >
                    <span className="block font-medium">{ar.label}</span>
                    <span className="block text-[10px] text-warm-grey">
                      {ar.ratio}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Platform selection */}
            <div className="space-y-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
                Platforms
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(
                  ["instagram", "facebook", "tiktok", "youtube"] as Platform[]
                ).map((platform) => {
                  const connection = supportedConnections.find(
                    (c) => c.platform === platform,
                  )
                  const isSelected = selectedPlatforms.has(platform)
                  const disabled = !connection

                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => !disabled && togglePlatform(platform)}
                      disabled={disabled}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                        disabled
                          ? "cursor-not-allowed border-stone/40 bg-stone/5 text-warm-grey/40"
                          : isSelected
                            ? "border-red/50 bg-red/5 text-charcoal"
                            : "border-stone/60 text-warm-grey hover:border-stone"
                      }`}
                    >
                      <span
                        className={`flex size-4 items-center justify-center rounded border text-[10px] ${
                          isSelected
                            ? "border-red bg-red text-white"
                            : "border-stone/70"
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="size-2.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={3}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m4.5 12.75 6 6 9-13.5"
                            />
                          </svg>
                        )}
                      </span>
                      <span className="font-medium">
                        {PLATFORM_LABELS[platform]}
                      </span>
                      {!connection && (
                        <span className="ml-auto text-[10px] text-warm-grey/50">
                          Not connected
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Captions */}
            {selectedPlatforms.size > 0 && (
              <PlatformCaptionTabs
                platforms={Array.from(selectedPlatforms)}
                captions={captions}
                onCaptionChange={(platform, value) =>
                  setCaptions((prev) => ({ ...prev, [platform]: value }))
                }
                onGenerateCaption={handleGenerateCaption}
                generatingCaption={generatingCaption}
                facebookPages={fbPages}
                facebookPageId={facebookPageId}
                onFacebookPageChange={setFacebookPageId}
                facebookPostType={facebookPostType}
                onFacebookPostTypeChange={setFacebookPostType}
                shareToFeed={shareToFeed}
                onShareToFeedChange={setShareToFeed}
                locationId={locationId}
                locationName={locationName}
                locationMeta={locationMeta}
                onLocationChange={(id, name, meta) => {
                  setLocationId(id)
                  setLocationName(name)
                  setLocationMeta(meta ?? null)
                }}
                showLocationPicker={!!currentUser?.is_superadmin}
                tiktokConnectionId={
                  selectedPlatforms.has("tiktok")
                    ? supportedConnections.find((c) => c.platform === "tiktok")?.id
                    : null
                }
                tiktokState={tiktokState}
                onTiktokStateChange={setTiktokState}
              />
            )}

            {/* Schedule toggle */}
            {selectedPlatforms.size > 0 && (
              <div className="space-y-3 border-t border-stone/20 pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="video-post-schedule-toggle"
                    checked={scheduleEnabled}
                    onCheckedChange={setScheduleEnabled}
                  />
                  <Label
                    htmlFor="video-post-schedule-toggle"
                    className="text-sm"
                  >
                    Schedule for later
                  </Label>
                </div>

                {scheduleEnabled && (
                  <DateTimePicker
                    value={scheduledAt}
                    onChange={setScheduledAt}
                    minDate={new Date()}
                    placeholder="Choose date & time"
                    className="w-auto"
                  />
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePublish}
                disabled={
                  publishing ||
                  selectedPlatforms.size === 0 ||
                  !videoFile ||
                  (scheduleEnabled && !scheduledAt) ||
                  (selectedPlatforms.has("tiktok") && !isTiktokStateValid(tiktokState))
                }
                className="bg-red px-6 text-white hover:bg-red/90"
              >
                {publishing
                  ? "Publishing..."
                  : scheduleEnabled
                    ? "Schedule"
                    : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? "s" : ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function NoConnectionsCallout() {
  return (
    <div className="rounded-lg border border-stone/30 bg-stone/5 px-4 py-5 text-center">
      <svg
        className="mx-auto mb-2 size-8 text-warm-grey/40"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
        />
      </svg>
      <p className="text-sm font-medium text-charcoal">
        No social accounts connected
      </p>
      <p className="mt-1 text-xs text-warm-grey">
        Connect your social media accounts to start publishing.
      </p>
      <a
        href="/dashboard/connections"
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-deep-red"
      >
        Connect accounts
        <svg
          className="size-3.5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
          />
        </svg>
      </a>
    </div>
  )
}
